import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchBlogsCMS, editBlogCMS, updateBlogCMS, trashBlogCMS,
} from "../../Adapters/ApiAdapter";
import { baseImage_path, baseUrl } from "../../variables/Variables";
import SeoTags from "../SeoTags/SeoTags";
import CKEditorField from "../../components/CKEditorField";
// import ReactQuill from "react-quill";
// CKEditor
// import { CKEditor } from "@ckeditor/ckeditor5-react";
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const BlogsList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editBlog, setEditBlog] = useState(null);
  const [deleteBlogId, setDeleteBlogId] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  // Fetch blogs on component mount
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const response = await fetchBlogsCMS();
        if (response?.data) {
          setBlogs((response.data).sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )
          );
        } else {
          setError("Failed to fetch blogs.");
        }
      } catch (err) {
        setError("An error occurred while fetching blogs.");
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editBlogCMS(id);
      console.log("Edit Blog response:", response.data);
      if (response && response.data) {
        const blogs = response.data;

        setEditBlog({
          id: blogs.id,
          title: blogs.title,
          slug: blogs.slug,
          category: blogs.category,
          content: blogs.content,
          source_page: blogs.source_page,
          status: blogs.status,
          speciality_id: blogs.speciality_id,
          seo_title: blogs.seo_title,
          meta_title: blogs.meta_title,
          meta_description: blogs.meta_description,
          meta_keywords: blogs.meta_keywords,
          custom_scripts: blogs.custom_scripts,
          og_title: blogs.og_title,
          og_locale: blogs.og_locale,
          og_description: blogs.og_description,
          og_type: blogs.og_type,
          og_url: blogs.og_url,
          og_image: blogs.og_image,
          og_site_name: blogs.og_site_name,
          og_image_width: blogs.og_image_width,
          og_image_height: blogs.og_image_height,
          og_image_type: blogs.og_image_type,
          article_modified_time: blogs.article_modified_time,
          canonical_link: blogs.canonical_link,
          twitter_card: blogs.twitter_card,
          twitter_site: blogs.twitter_site,
          twitter_title: blogs.twitter_title,
          twitter_description: blogs.twitter_description,
          twitter_image: blogs.twitter_image,
          featured_img: blogs.featured_img,
          banner_img: blogs.banner_img,
        });
      } else {
        toast.error("Failed to load blog for editing.");
      }
    } catch (err) {
      console.error("Error loading edit data:", err);
      toast.error("Failed to load blog for editing.");
    }
  };

  const handleSaveEdit = async () => {
    setLoadingAction(true);
    const formData = new FormData();
    // const stripHtml = (html) => html.replace(/<[^>]+>/g, "").trim();

    const isValidDate = (d) => {
      return d && !isNaN(new Date(d).getTime());
    };

    formData.append("title", editBlog.title);
    formData.append("slug", editBlog.slug);
    formData.append("category", editBlog.category || "");
    formData.append("content", editBlog.content || "");
    formData.append("source_page", editBlog.source_page || "");
    formData.append("status", editBlog.status || "");
    formData.append("speciality_id", editBlog.speciality_id || "");
    formData.append("seo_title", editBlog.seo_title || "");
    formData.append("meta_title", editBlog.meta_title || "");
    formData.append("meta_description", editBlog.meta_description || "");
    formData.append("meta_keywords", editBlog.meta_keywords || "");
    formData.append("custom_scripts", editBlog.custom_scripts || "");
    formData.append("og_title", editBlog.og_title || "");
    formData.append("og_locale", editBlog.og_locale || "");
    formData.append("og_description", editBlog.og_description || "");
    formData.append("og_type", editBlog.og_type || "");
    formData.append("og_url", editBlog.og_url || "");
    formData.append("og_site_name", editBlog.og_site_name || "");
    // formData.append("og_image_width", editBlog.og_image_width);
    // formData.append("og_image_height", editBlog.og_image_height);
    formData.append("og_image_width", parseInt(editBlog.og_image_width || 0));
    formData.append("og_image_height", parseInt(editBlog.og_image_height || 0));
    formData.append("og_image_type", editBlog.og_image_type);
    // formData.append("article_modified_time", editBlog.article_modified_time);
    formData.append(
      "article_modified_time",
      isValidDate(editBlog.article_modified_time)
        ? new Date(editBlog.article_modified_time).toISOString().split("T")[0]
        : ""
    );
    formData.append("canonical_link", editBlog.canonical_link);
    formData.append("twitter_card", editBlog.twitter_card);
    formData.append("twitter_site", editBlog.twitter_site);
    formData.append("twitter_title", editBlog.twitter_title);
    formData.append("twitter_description", editBlog.twitter_description);

    if (editBlog.featured_img instanceof File) {
      formData.append("featured_img", editBlog.featured_img);
    }
    if (editBlog.bannner_img instanceof File) {
      formData.append("banner_img", editBlog.banner_img);
    }
    if (editBlog.og_image instanceof File) {
      formData.append("og_image", editBlog.og_image);
    }
    if (editBlog.twitter_image instanceof File) {
      formData.append("twitter_image", editBlog.twitter_image);
    }

    try {
      await updateBlogCMS(editBlog.id, formData);
      toast.success("Blog updated successfully!");
      setBlogs((prev) =>
        prev.map((blog) =>
          blog.id === editBlog.id
            ? {
              ...blog,
              title: editBlog.title,
              slug: editBlog.slug,
              category: editBlog.category,
              content: editBlog.content,
              source_page: editBlog.source_page,
              status: editBlog.status,
              speciality_id: editBlog.speciality_id,
              seo_title: editBlog.seo_title,
              meta_title: editBlog.meta_title,
              meta_description: editBlog.meta_description,
              meta_keywords: editBlog.meta_keywords,
              custom_scripts: editBlog.custom_scripts,
              og_title: editBlog.og_title,
              og_locale: editBlog.og_locale,
              og_description: editBlog.og_description,
              og_type: editBlog.og_type,
              og_url: editBlog.og_url,
              og_site_name: editBlog.og_site_name,
              og_image_width: editBlog.og_image_width,
              og_image_height: editBlog.og_image_height,
              og_image_type: editBlog.og_image_type,
              og_image: editBlog.og_image,
              article_modified_time: editBlog.article_modified_time,
              canonical_link: editBlog.canonical_link,
              twitter_card: editBlog.twitter_card,
              twitter_site: editBlog.twitter_site,
              twitter_title: editBlog.twitter_title,
              twitter_description: editBlog.twitter_description,
              twitter_image: editBlog.twitter_image,
              featured_img: editBlog.featured_img,
              banner_img: editBlog.banner_img,
            }
            : blog
        )
      );
      setEditBlog(null); // Close dialog
    } catch (err) {
      console.error("Error saving edit:", err);
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setLoadingAction(false);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/&/g, " ")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");
  };
  const handleDelete = async (id) => {
    setLoadingAction(true);

    try {
      const response = await trashBlogCMS(id);
      if (response && response.status === "true") {
        toast.success("Blog deleted successfully!");
        setBlogs((prev) => prev.filter((blog) => blog.id !== id));
        setDeleteBlogId(null); // Close dialog
      } else {
        toast.error("Failed to delete Blog");
      }
    } catch (err) {
      console.error("Error deleting blog:", err);
      toast.error("Failed to delete blog. Please try again.");
    } finally {
      setLoadingAction(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "title", headerName: "Title", width: 200 },
    { field: "slug", headerName: "Slug", width: 150 },
    { field: "category", headerName: "Category", width: 150 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "created_at",
      headerName: "Created At",
      width: 200,
      valueFormatter: (params) =>
        dayjs(params.value).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      field: "featured_img",
      headerName: "Featured Image",
      width: 150,
      renderCell: (params) => (
        <img
          src={`${baseImage_path}/${params.row.featured_img}`} // Append the base image path
          alt={params.row.title}
          style={{ width: "100px", height: "auto", borderRadius: "4px" }} // Style the image
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteBlogId(params.row.id)}
            style={{ marginLeft: "8px" }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Blogs List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGrid rows={blogs} columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      {editBlog && (
        <Dialog open onClose={() => setEditBlog(null)}>
          <DialogTitle>Edit Blog</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Title"
              value={editBlog.title}
              onChange={(e) => {
                const newTitle = e.target.value;
                setEditBlog({
                  ...editBlog,
                  title: newTitle,
                  slug: generateSlug(newTitle),
                });
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Slug"
              value={editBlog.slug}
              onChange={(e) =>
                setEditBlog({ ...editBlog, slug: e.target.value })
              }
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={editBlog.category}
                // onChange={(e) => handleInputChange(e.target.value)}
                onChange={(e) =>
                  setEditBlog({ ...editBlog, category: e.target.value })
                }
                required
              >
                <MenuItem value="Tech">Tech</MenuItem>
                <MenuItem value="Health">Health</MenuItem>
                <MenuItem value="Lifestyle">Lifestyle</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body1">Content</Typography>
	      {/*  <ReactQuill
              theme="snow"
              value={editBlog.content}
              onChange={(content) =>
                setEditBlog({ ...editBlog, content: content })
              }
              style={{ height: "200px", marginBottom: "50px" }}
            /> */}
	      <Box sx={{ ".ck-editor__editable_inline": { minHeight: "300px" } }}>
              {/* <CKEditor
                editor={ClassicEditor}
                data={editBlog.content}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setEditBlog({ ...editBlog, content: data });
                }}
                config={{
                  toolbar: [
                    "heading", "|",
                    "bold", "italic", "underline", "strikethrough", "|",
                    "link", "blockQuote", "codeBlock", "|",
                    "bulletedList", "numberedList", "outdent", "indent", "|",
                    "insertTable", "undo", "redo", "sourceEditing"
                  ],
                  table: {
                    contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"]
                  }
                }}
              /> */}
              <CKEditorField
            value={editBlog.content}
            onChange={(data) => setEditBlog({ ...editBlog, content: data })}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=blogs`}
          />
            </Box>

            <TextField
              fullWidth
              margin="normal"
              label="Source Page"
              value={editBlog.source_page || ""}
              onChange={(e) =>
                setEditBlog({ ...editBlog, source_page: e.target.value })
              }
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={editBlog.status}
                // onChange={(e) => handleInputChange(e.target.value)}
                onChange={(e) =>
                  setEditBlog({ ...editBlog, status: e.target.value })
                }
                required
              >
                <MenuItem value="publish">Publish</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>

            <SeoTags formData={editBlog} setFormData={setEditBlog} />

            <Box
              mt={2}
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Button
                variant="contained"
                component="label"
                style={{ marginTop: "20px" }}
              >
                Featured Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) =>
                    setEditBlog({
                      ...editBlog,
                      featured_img:
                        e.target.files.length > 0
                          ? e.target.files[0]
                          : editBlog.featured_img,
                    })
                  }
                />
              </Button>
              {editBlog.featured_img && (
                <Box mt={2} mb={2} textAlign="center">
                  <img
                    src={`${baseImage_path}/${editBlog.featured_img}`}
                    alt="Featured"
                    style={{
                      width: "100px",
                      height: "auto",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditBlog(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
              s
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEdit}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteBlogId && (
        <Dialog open onClose={() => setDeleteBlogId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this blog?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteBlogId(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteBlogId)}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default BlogsList;
